import React from "react";
import {
  Heart,
  Euro,
  Package as PackageIcon,
  ExternalLink,
} from "lucide-react";
import { useFavorites } from "../hooks";
import { Card, ModernCardHeader } from "../../../components/cards";
import { FavoriteButton } from "./favorite-button";
import { useNavigate } from "react-router-dom";

export const FavoritesDashboardCard: React.FC = () => {
  const { data, loading } = useFavorites();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <ModernCardHeader
          title="Produits Favoris"
          subtitle="Vos produits préférés"
        />
        <div className="text-center py-8 text-gray-500 text-sm">
          Chargement...
        </div>
      </Card>
    );
  }

  const favorites = data?.favorites?.results || [];
  const displayedFavorites = favorites.slice(0, 4);

  return (
    <Card>
      <ModernCardHeader
        title="Produits Favoris"
        subtitle={
          favorites.length > 0
            ? `${favorites.length} produit${
                favorites.length > 1 ? "s" : ""
              } en favoris`
            : "Aucun produit favori"
        }
      />

      {displayedFavorites.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          <Heart size={48} className="mx-auto mb-3 text-gray-300" />
          <p>Aucun produit en favoris</p>
          <p className="text-xs mt-2">
            Ajoutez des produits en cliquant sur l'icône ❤️
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {displayedFavorites.map((favorite: any) => {
              const product = favorite.product;
              if (!product) return null;

              return (
                <div
                  key={favorite.id}
                  className="relative group cursor-pointer"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <Card
                    hover
                    padding="sm"
                    className="h-full transition-all border border-gray-200"
                  >
                    <div className="absolute top-2 right-2 z-10">
                      <FavoriteButton productId={product.id} size={16} />
                    </div>

                    {/* Image */}
                    <div className="aspect-square mb-2 overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
                      {product.imgUrl ? (
                        <img
                          src={product.imgUrl}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <PackageIcon size={32} className="text-gray-300" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="space-y-1">
                      <h3 className="font-medium text-gray-900 text-xs line-clamp-2 group-hover:text-primary-600 transition-colors min-h-[2rem]">
                        {product.name}
                      </h3>

                      {product.site && (
                        <p className="text-[10px] text-gray-500 flex items-center gap-1 truncate">
                          <ExternalLink size={10} />
                          {product.site.name}
                        </p>
                      )}

                      <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                        <div className="flex items-center gap-0.5 text-blue-600 font-semibold text-sm">
                          <Euro size={12} />
                          <span>{product.lastPrice.toFixed(2)}</span>
                        </div>

                        {!product.priceOnly && (
                          <div className="text-[10px] text-gray-500">
                            Stock: {product.lastQuantity}
                          </div>
                        )}

                        {product.priceOnly && (
                          <div className="text-[10px] text-orange-600 font-medium">
                            Prix only
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>

          {favorites.length > 4 && (
            <div className="mt-4 text-center">
              <a
                href="/favorites"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                Voir tous les favoris ({favorites.length}) →
              </a>
            </div>
          )}
        </>
      )}
    </Card>
  );
};
